import { useRef, type ReactNode } from "react";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
  type UniqueIdentifier,
} from "@dnd-kit/core";
import { dispatchScheduleEvent } from "../../lib/schedule-history/dispatch";
import { readSwapDrop, sameKindCollision } from "./schedule-swap";
import {
  animateReturnRelease,
  animateSwapRelease,
} from "./schedule-swap-animation";

const pointerActivation = { distance: 4 };

export function ScheduleSwapArea({ children }: { children: ReactNode }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: pointerActivation,
  });
  const sensors = useSensors(pointerSensor);

  const findRowNode = (dragId: UniqueIdentifier) => {
    const selector = `[data-swap-id="${dragId}"]`;
    const container = containerRef.current;
    return container?.querySelector<HTMLElement>(selector) ?? undefined;
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const sourceNode = findRowNode(event.active.id);
    if (!sourceNode) return;
    const drop = readSwapDrop(event);
    if (!drop) {
      animateReturnRelease(sourceNode);
      return;
    }
    const targetNode = findRowNode(drop.targetId);
    if (!targetNode) return;
    // Animation starts before the swap renders, so its first frame shows
    // the swapped content.
    animateSwapRelease(sourceNode, targetNode);
    if (drop.source.kind === "slot") {
      dispatchScheduleEvent({ type: "slot/swap", sourceKey: drop.source.storeKey, targetKey: drop.target.storeKey });
      return;
    }
    dispatchScheduleEvent({ type: "offline/swap", sourceDate: drop.source.storeKey, targetDate: drop.target.storeKey });
  };

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={sameKindCollision}
      onDragEnd={handleDragEnd}
    >
      <div ref={containerRef}>{children}</div>
    </DndContext>
  );
}
